import React, { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, ArrowDown, Gavel, Stethoscope, Users, Globe2, Handshake,
  Building2, GraduationCap, HeartHandshake, Megaphone, Check, Loader2, CheckCircle2, Upload,
} from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const GREEN = "#1F4D2E";

const COUNTRIES = [
  "Ethiopia", "United States", "Canada", "United Kingdom", "Germany", "Sweden", "Norway",
  "Netherlands", "Australia", "United Arab Emirates", "Saudi Arabia", "Kenya", "South Africa",
  "Israel", "Italy", "France", "Switzerland", "Ireland", "Belgium", "Denmark", "Finland",
  "Austria", "Spain", "Qatar", "Kuwait", "Djibouti", "Sudan", "Egypt", "India", "China",
  "Japan", "South Korea", "Brazil", "Mexico", "New Zealand", "Turkey", "Nigeria", "Ghana",
  "Uganda", "Tanzania", "Rwanda", "Other",
];

const EXPERTISE_TAGS = ["Law", "Medicine", "Counseling", "Social Work", "Education",
  "Nonprofit Leadership", "Grants", "Fundraising", "Business", "Finance", "Marketing",
  "Social Media", "Government / Public Service", "Economic Development", "Ethiopian Diaspora"];

const PARTICIPATION = [
  "Board of Directors", "Professional Advisory Council", "Government/Public-Sector Advisor",
  "Ethiopian Diaspora Network", "Global Ambassador / Influencer",
];
const AREAS = [
  "Legal", "Healthcare", "Counseling / Mental Health", "Social Work", "Education",
  "Grant Development", "Fundraising", "Nonprofit Management", "Business / Entrepreneurship",
  "Finance / Accounting", "Marketing / Communications", "Mercy Made / Social Enterprise",
  "Corporate Partnership", "NGO Partnership", "Volunteer Professional", "Other",
];

const ROLES = [
  {
    icon: Gavel, title: "Founding Board of Directors",
    body: "Seeking experienced professionals and community leaders willing to provide governance, strategic leadership, financial oversight, professional expertise, fundraising support, partnerships and community connections.",
    tags: ["Governance", "Strategic Leadership", "Financial Oversight", "Fundraising", "Partnerships"],
    note: "Board participation is initially voluntary / unpaid professional service.",
  },
  {
    icon: Stethoscope, title: "Professional & Public-Sector Advisory Council",
    body: "Lawyers · Doctors, Nurses & Public Health · Counselors, Therapists & Psychologists · Social Workers · Educators · Nonprofit Executives · Grant & Fundraising Professionals · Business Owners & Retired Executives · Accountants & Finance · Marketing & PR · Economic Development · qualified current or former Government / Public-Sector professionals.",
    tags: ["Advise", "Guide", "Review", "Connect"],
    note: "Participation by current government / public-sector professionals is subject to applicable laws, ethics requirements and conflict-of-interest rules.",
  },
  {
    icon: Megaphone, title: "Refugium Global Ambassadors",
    body: "Actively recruiting Ethiopian and diaspora influencers, content creators, artists, athletes, entrepreneurs, community leaders and public personalities to raise awareness and connect communities.",
    tags: ["Raise Awareness", "Share Campaigns", "Attend Events", "Introduce Partners", "Promote Fundraising"],
    note: "Ambassadors do not automatically become board members.",
  },
];

const inputCls = "w-full border border-[#1F4D2E]/20 rounded-lg px-4 py-3 text-[15px] bg-white focus:outline-none focus:ring-2 focus:ring-[#1F4D2E]/40 focus:border-[#1F4D2E]";

function Section({ children, className = "" }) {
  return <section className={`max-w-7xl mx-auto px-6 lg:px-10 ${className}`}>{children}</section>;
}

export default function FoundingNetworkPage() {
  const [params] = useSearchParams();
  const preselect = params.get("interest");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cvBusy, setCvBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "", city: "", country: "", profession: "", current_role: "",
    organization: "", linkedin: "", website: "", social: "", cv_url: "",
    interests: preselect && [...PARTICIPATION, ...AREAS].includes(preselect) ? [preselect] : [],
    why: "", contribution: "", time_commitment: "", video_available: "",
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleInterest = (i) =>
    setForm((f) => ({ ...f, interests: f.interests.includes(i) ? f.interests.filter((x) => x !== i) : [...f.interests, i] }));

  const scrollToApply = () => document.getElementById("apply")?.scrollIntoView({ behavior: "smooth" });

  const uploadCv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCvBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/founding-applications/upload-cv", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("cv_url", data.url);
      toast.success("CV uploaded");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setCvBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/founding-applications", form);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (done) {
    return (
      <div data-testid="founding-thankyou" className="min-h-[70vh] flex items-center justify-center px-6 py-24">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl text-center">
          <span className="w-20 h-20 rounded-full bg-[#16A34A]/15 text-[#16A34A] flex items-center justify-center mx-auto mb-6"><CheckCircle2 size={40} /></span>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1F4D2E] mb-4">Thank You for Your Interest</h1>
          <p className="text-lg text-[#4F6B54] leading-relaxed">Thank you for your interest in helping build Refugium Ethiopia. Our team will review your information and contact selected applicants regarding opportunities to become involved.</p>
          <Link to="/" className="mt-8 inline-flex items-center gap-2 bg-[#1F4D2E] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#C1272D] transition-colors" data-testid="founding-home-btn">Back to home <ArrowRight size={18} /></Link>
        </motion.div>
      </div>
    );
  }

  return (
    <div data-testid="founding-page">
      {/* HERO */}
      <section className="relative bg-[#1F4D2E] text-white overflow-hidden">
        <span className="absolute -top-20 -right-16 w-80 h-80 rounded-full bg-[#E0A526]/10" />
        <span className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-[#C1272D]/15" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-28 pb-24">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E0A526] mb-5">Join Our Founding Network</p>
            <h1 className="font-display text-5xl sm:text-6xl font-bold leading-[0.98] tracking-tight">Help Build Something That Lasts.</h1>
            <p className="text-lg text-[#E7EDE2]/90 mt-6 leading-relaxed">Refugium Ethiopia is building its founding leadership and professional network. We are seeking approximately <strong className="text-white">5&ndash;10 founding board members</strong>, plus professional advisors, ambassadors and partners willing to contribute their experience, expertise, connections and time toward creating lasting opportunities for children, families and communities in Ethiopia.</p>
            <div className="flex flex-wrap gap-3 mt-8">
              {EXPERTISE_TAGS.map((t) => (
                <span key={t} className="text-[13px] font-medium bg-white/10 border border-white/15 rounded-full px-4 py-1.5">{t}</span>
              ))}
            </div>
            <button onClick={scrollToApply} data-testid="founding-hero-cta" className="mt-9 inline-flex items-center gap-2 bg-[#C1272D] text-white rounded-full px-8 py-4 font-semibold tracking-wide hover:bg-[#9E1F24] transition-colors">
              Join Our Founding Network <ArrowDown size={18} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* ROLE SECTIONS */}
      <Section className="py-24">
        <div className="max-w-2xl mb-14">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Ways to lead</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">Three ways experienced people can help build Refugium.</h2>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {ROLES.map((r, i) => (
            <motion.div key={r.title} initial={{ opacity: 0, y: 28 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-[#FBF8F0] border border-[#1F4D2E]/10 p-9 flex flex-col" data-testid={`role-card-${i}`}>
              <span className="w-14 h-14 rounded-full bg-[#E0A526]/20 text-[#1F4D2E] flex items-center justify-center mb-6"><r.icon size={26} /></span>
              <h3 className="font-display text-2xl font-semibold text-[#1F4D2E] mb-3">{r.title}</h3>
              <p className="text-[#4F6B54] leading-relaxed text-[15px] mb-5 flex-1">{r.body}</p>
              <div className="flex flex-wrap gap-2 mb-5">
                {r.tags.map((t) => <span key={t} className="text-xs font-medium bg-[#1F4D2E]/8 text-[#1F4D2E] rounded-full px-3 py-1">{t}</span>)}
              </div>
              <p className="text-xs text-[#4F6B54]/80 italic border-t border-[#1F4D2E]/10 pt-4">{r.note}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      {/* DIASPORA */}
      <section className="bg-[#C1272D] text-white relative overflow-hidden" data-testid="diaspora-section">
        <span className="absolute -top-16 -right-10 w-72 h-72 rounded-full bg-white/5" />
        <div className="relative max-w-6xl mx-auto px-6 lg:px-10 py-24">
          <Globe2 size={40} className="text-white/80 mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl font-bold leading-tight max-w-3xl">You don't have to live in Ethiopia to help build its future.</h2>
          <p className="text-white/90 mt-6 max-w-2xl leading-relaxed text-lg">Refugium Ethiopia welcomes Ethiopian professionals, entrepreneurs and community leaders throughout the global diaspora who want to contribute their knowledge, experience, networks and resources toward sustainable impact in Ethiopia.</p>
          <div className="flex flex-wrap gap-3 mt-8">
            {["Give", "Mentor", "Advise", "Connect", "Advocate", "Partner", "Build"].map((t) => (
              <span key={t} className="text-sm font-semibold bg-white/15 border border-white/20 rounded-full px-5 py-2">{t}</span>
            ))}
          </div>
          <button onClick={scrollToApply} data-testid="diaspora-cta"
            className="mt-9 inline-flex items-center gap-2 bg-white text-[#C1272D] rounded-full px-8 py-4 font-semibold hover:bg-[#1F4D2E] hover:text-white transition-colors">
            Join the Diaspora Network <ArrowRight size={18} />
          </button>
        </div>
      </section>

      {/* GOVERNMENT / INSTITUTIONAL PARTNERSHIP */}
      <Section className="py-24" >
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start" data-testid="partnership-section">
          <div className="lg:col-span-5">
            <span className="w-14 h-14 rounded-full bg-[#1F4D2E]/10 text-[#1F4D2E] flex items-center justify-center mb-6"><Building2 size={26} /></span>
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Institutional partnership</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">Building impact through collaboration.</h2>
            <p className="text-[#4F6B54] mt-5 leading-relaxed">Refugium Ethiopia welcomes responsible collaboration with organisations and institutions that share our commitment to children, families and communities.</p>
            <button onClick={scrollToApply} data-testid="partnership-cta" className="mt-7 inline-flex items-center gap-2 bg-[#1F4D2E] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#C1272D] transition-colors">
              Discuss a Partnership <Handshake size={18} />
            </button>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
            {["Ethiopian Government Institutions", "Local Government", "NGOs & Nonprofits", "Schools & Universities",
              "Healthcare Organizations", "Businesses", "Foundations", "International Development Organizations",
              "Diaspora Organizations"].map((o) => (
              <div key={o} className="flex items-start gap-3 bg-[#FBF8F0] border border-[#1F4D2E]/10 rounded-lg px-4 py-3.5">
                <Check size={18} className="text-[#16A34A] shrink-0 mt-0.5" />
                <span className="text-[#1F4D2E] text-[15px] font-medium">{o}</span>
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-[#4F6B54]/70 mt-8 max-w-3xl">Refugium Ethiopia does not currently claim government endorsement; partnerships are described here as opportunities for future collaboration.</p>
      </Section>

      {/* APPLICATION FORM */}
      <section id="apply" className="bg-[#E7EDE2] scroll-mt-24" data-testid="apply-section">
        <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24">
          <div className="mb-10">
            <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">Become part of Refugium Ethiopia</p>
            <h2 className="font-display text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F4D2E] leading-tight">Tell us how your experience can help build the mission.</h2>
          </div>

          <form onSubmit={submit} className="bg-white border border-[#1F4D2E]/10 rounded-2xl p-7 sm:p-10 space-y-6" data-testid="founding-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">Full name *</span>
                <input required className={`${inputCls} mt-1.5`} value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="f-name" /></label>
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">Email *</span>
                <input required type="email" className={`${inputCls} mt-1.5`} value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="f-email" /></label>
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">Phone / WhatsApp</span>
                <input type="tel" placeholder="+251 ..." className={`${inputCls} mt-1.5`} value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="f-phone" /></label>
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">City</span>
                <input className={`${inputCls} mt-1.5`} value={form.city} onChange={(e) => set("city", e.target.value)} data-testid="f-city" /></label>
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">Country</span>
                <input list="country-list" className={`${inputCls} mt-1.5`} value={form.country} onChange={(e) => set("country", e.target.value)} data-testid="f-country" />
                <datalist id="country-list">{COUNTRIES.map((c) => <option key={c} value={c} />)}</datalist></label>
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">Profession</span>
                <input className={`${inputCls} mt-1.5`} value={form.profession} onChange={(e) => set("profession", e.target.value)} data-testid="f-profession" /></label>
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">Current role</span>
                <input className={`${inputCls} mt-1.5`} value={form.current_role} onChange={(e) => set("current_role", e.target.value)} data-testid="f-role" /></label>
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">Company / Organization</span>
                <input className={`${inputCls} mt-1.5`} value={form.organization} onChange={(e) => set("organization", e.target.value)} data-testid="f-org" /></label>
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">LinkedIn <span className="text-[#4F6B54]/60 font-normal">(optional)</span></span>
                <input className={`${inputCls} mt-1.5`} value={form.linkedin} onChange={(e) => set("linkedin", e.target.value)} data-testid="f-linkedin" /></label>
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">Website <span className="text-[#4F6B54]/60 font-normal">(optional)</span></span>
                <input className={`${inputCls} mt-1.5`} value={form.website} onChange={(e) => set("website", e.target.value)} data-testid="f-website" /></label>
              <label className="block sm:col-span-2"><span className="text-sm font-semibold text-[#1F4D2E]">Social media <span className="text-[#4F6B54]/60 font-normal">(optional)</span></span>
                <input className={`${inputCls} mt-1.5`} value={form.social} onChange={(e) => set("social", e.target.value)} data-testid="f-social" /></label>
            </div>

            {/* CV upload */}
            <div>
              <span className="text-sm font-semibold text-[#1F4D2E]">Upload CV / Resume <span className="text-[#4F6B54]/60 font-normal">(optional — PDF or Word)</span></span>
              <div className="flex items-center gap-3 mt-1.5">
                <label className="inline-flex items-center gap-2 border border-[#1F4D2E]/25 rounded-lg px-4 py-2.5 text-sm font-medium text-[#1F4D2E] cursor-pointer hover:bg-[#FBF8F0]" data-testid="f-cv-btn">
                  {cvBusy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {cvBusy ? "Uploading…" : "Choose file"}
                  <input type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={uploadCv} data-testid="f-cv-input" />
                </label>
                {form.cv_url && <span className="text-xs text-[#16A34A] font-medium inline-flex items-center gap-1"><Check size={14} /> Uploaded</span>}
              </div>
            </div>

            {/* Interests */}
            <div>
              <span className="text-sm font-semibold text-[#1F4D2E]">I am interested in: <span className="text-[#4F6B54]/60 font-normal">(select all that apply)</span></span>
              <p className="text-xs uppercase tracking-wider font-bold text-[#C1272D] mt-4 mb-2">Participation</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {PARTICIPATION.map((i) => <InterestCheck key={i} label={i} checked={form.interests.includes(i)} onToggle={() => toggleInterest(i)} />)}
              </div>
              <p className="text-xs uppercase tracking-wider font-bold text-[#C1272D] mt-5 mb-2">Areas of expertise</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {AREAS.map((i) => <InterestCheck key={i} label={i} checked={form.interests.includes(i)} onToggle={() => toggleInterest(i)} />)}
              </div>
            </div>

            <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">Why would you like to become involved with Refugium Ethiopia?</span>
              <textarea rows={3} className={`${inputCls} mt-1.5`} value={form.why} onChange={(e) => set("why", e.target.value)} data-testid="f-why" /></label>
            <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">What professional experience, expertise, resources or connections could you contribute?</span>
              <textarea rows={3} className={`${inputCls} mt-1.5`} value={form.contribution} onChange={(e) => set("contribution", e.target.value)} data-testid="f-contribution" /></label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">How much time could you reasonably volunteer?</span>
                <input className={`${inputCls} mt-1.5`} placeholder="e.g. 2–4 hours / month" value={form.time_commitment} onChange={(e) => set("time_commitment", e.target.value)} data-testid="f-time" /></label>
              <label className="block"><span className="text-sm font-semibold text-[#1F4D2E]">Available for an introductory video meeting?</span>
                <select className={`${inputCls} mt-1.5`} value={form.video_available} onChange={(e) => set("video_available", e.target.value)} data-testid="f-video">
                  <option value="">Please choose…</option>
                  <option>Yes</option><option>Maybe</option><option>No</option>
                </select></label>
            </div>

            <button type="submit" disabled={busy} data-testid="founding-submit"
              className="w-full inline-flex items-center justify-center gap-2 bg-[#C1272D] text-white rounded-full py-4 font-semibold tracking-wide hover:bg-[#9E1F24] transition-colors disabled:opacity-60">
              {busy ? <><Loader2 size={18} className="animate-spin" /> Submitting…</> : <>Submit My Interest <ArrowRight size={18} /></>}
            </button>
            <p className="text-xs text-[#4F6B54]/70 text-center">We use the term <strong>Founding Leaders, Advisors, Ambassadors &amp; Partners</strong> — never "investors." Your information is kept confidential.</p>
          </form>
        </div>
      </section>
    </div>
  );
}

function InterestCheck({ label, checked, onToggle }) {
  return (
    <button type="button" onClick={onToggle} data-testid={`f-interest-${label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
      className={`flex items-center gap-2.5 text-left rounded-lg border px-3.5 py-2.5 text-[14px] transition-colors ${checked ? "bg-[#1F4D2E] text-white border-[#1F4D2E]" : "bg-white text-[#1F4D2E] border-[#1F4D2E]/20 hover:border-[#1F4D2E]/50"}`}>
      <span className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${checked ? "bg-white border-white" : "border-[#1F4D2E]/40"}`}>
        {checked && <Check size={12} className="text-[#1F4D2E]" />}
      </span>
      {label}
    </button>
  );
}
